import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAtrasado } from "../componentes/atraso.js";
import { CabecalhoDePagina } from "../componentes/CabecalhoDePagina.js";
import { Link, useNavigate } from "react-router";
import type { EstudoAtual, MontarEntrada, OpcoesEstudo, TipoItem } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { IconeSeguir } from "../componentes/icones.js";
import { CaixaMao } from "../componentes/Tracos.js";
import { grauDaDificuldade } from "./BarraDeAcoes.js";
import { NOME_DO_TIPO } from "../rotulos.js";
import { SeletorMultiplo } from "../componentes/SeletorMultiplo.js";
import { Contador } from "../componentes/Controles.js";
import { useSessao } from "../estado/sessao.js";
import { useRitual } from "../estado/ritual.js";
import { FimDeSessao, resumoDoAtual, type RegistroDaSessao, type ResumoDoFim } from "../componentes/FimDeSessao.js";
import { useTrilhaAtiva, useTrilhas } from "../estado/trilhas.js";
import { Executar } from "./Executar.js";

/**
 * Estudar. Três telas em uma, decididas pelo estado do servidor:
 *
 *  · sessão aberta com item → a execução;
 *  · sessão aberta sem item → o resultado da sessão que acabou;
 *  · sem sessão → a montagem, com os filtros da RN-09.
 *
 * A retomada (RN-10) sai de graça disso: o cursor está no banco, então abrir
 * esta rota em qualquer aparelho cai onde a última resposta parou. Não há nada
 * guardado no navegador para sincronizar.
 */


type Estado =
  | { nome: "carregando" }
  | { nome: "pronto"; atual: EstudoAtual; opcoes: OpcoesEstudo }
  | { nome: "erro"; mensagem: string };

export function Estudar() {
  const trilha = useTrilhaAtiva();
  const { estado: estadoTrilhas } = useTrilhas();
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });
  const { definir: definirOfensiva } = useRitual();
  // O resumo desta sessão, para o fim (8d). Some ao recarregar — é resumo, não
  // registro; o registro é a resposta no banco.
  const [registros, setRegistros] = useState<RegistroDaSessao[]>([]);
  // A sessão que acabou agora. O servidor conclui a sessão na última resposta
  // e devolve `sessao: null`; sem isto a tela pularia direto para a montagem.
  const [fim, setFim] = useState<ResumoDoFim | null>(null);
  const navegar = useNavigate();
  const mostrarEsqueleto = useAtrasado(estado.nome === "carregando");

  const carregar = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      const [atual, opcoes] = await Promise.all([pedir("estudo.ver"), pedir("estudo.opcoes")]);
      setEstado({ nome: "pronto", atual, opcoes });
    } catch (e) {
      setEstado({
        nome: "erro",
        mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível carregar o estudo.",
      });
    }
  }, []);

  useEffect(() => {
    if (estadoTrilhas.nome !== "pronto") return;
    if (!estadoTrilhas.ativaId) {
      setEstado({ nome: "erro", mensagem: "Escolha uma trilha antes de estudar." });
      return;
    }
    void carregar();
  }, [carregar, estadoTrilhas]);

  if (estado.nome === "carregando") {
    return (
      <Moldura>
        <div aria-busy="true" data-esqueleto={mostrarEsqueleto ? "visivel" : "mudo"}>
          <span className="so-leitor" role="status">
            Preparando o estudo
          </span>
          <div className="esqueleto esqueleto--titulo" />
          <div className="esqueleto esqueleto--bloco" />
        </div>
      </Moldura>
    );
  }

  if (estado.nome === "erro") {
    // Sem trilha ativa não é falha do sistema: é o estado guiado da RN-04
    // chegando aqui. Por isso a saída é um caminho, não um "tentar de novo".
    const semTrilha = !trilha;
    return (
      <Moldura>
        <section className={semTrilha ? "estado" : "estado estado--erro"} role={semTrilha ? undefined : "alert"}>
          <h2 className="estado__titulo">{semTrilha ? "Nenhuma trilha em uso" : "Não deu para começar"}</h2>
          <p className="estado__texto">
            {semTrilha
              ? "Estudar acontece sempre dentro de uma trilha: é ela que define quais itens entram na sessão."
              : estado.mensagem}
          </p>
          {semTrilha ? (
            <Link className="botao" to="/trilhas">
              Escolher uma trilha
            </Link>
          ) : (
            <button className="botao" type="button" onClick={() => void carregar()}>
              Tentar de novo
            </button>
          )}
        </section>
      </Moldura>
    );
  }

  const { atual, opcoes } = estado;
  const aplicar = (novo: EstudoAtual) => setEstado({ nome: "pronto", atual: novo, opcoes });
  const aplicarDaExecucao = (novo: EstudoAtual) => {
    if (atual.sessao && !novo.sessao && registros.length > 0) {
      setFim({
        respondidos: registros.length,
        acertos: registros.filter((r) => r.acertou).length,
        iniciadaEm: atual.sessao.iniciadaEm,
        respondidosHoje: novo.respondidosHoje,
        metaDiaria: novo.metaDiaria,
      });
    }
    aplicar(novo);
  };

  if (fim) {
    return (
      <FimDeSessao
        atual={fim}
        registros={registros}
        encerrando={false}
        aoEncerrar={() => navegar("/")}
        aoEstudarMais={() => {
          void pedir("estudo.montar", { corpo: { quantidade: fim.metaDiaria } })
            .then((r) => {
              setFim(null);
              setRegistros([]);
              if (r.montada) aplicar(r.atual);
              else void carregar();
            })
            .catch(() => {
              setFim(null);
              void carregar();
            });
        }}
      />
    );
  }
  const respondido = (novo: EstudoAtual) => {
    // A resposta que cruza a meta empurra a ofensiva nova para o cabeçalho; a
    // festa fica para o fim da sessão (8d).
    if (atual.respondidosHoje < atual.metaDiaria && novo.respondidosHoje >= novo.metaDiaria) {
      void pedir("ritual.ofensiva")
        .then((r) => {
          if (r.ofensiva) definirOfensiva(r.ofensiva);
        })
        .catch(() => undefined);
    }
  };

  if (atual.sessao && atual.item) {
    return (
      <Executar
        atual={atual}
        aoMudar={aplicarDaExecucao}
        aoResponder={respondido}
        aoRegistrar={(r) => setRegistros((todos) => [...todos.filter((x) => x.itemId !== r.itemId), r])}
        // RN-54: já confirmado pela barra. Encerrar descarta a fila montada e
        // leva à montagem.
        aoEncerrar={() => {
          setRegistros([]);
          void pedir("estudo.encerrar", { corpo: { motivo: "abandonada" } })
            .then(() => carregar())
            .catch(() => carregar());
        }}
      />
    );
  }

  if (atual.sessao && !atual.item) {
    return (
      <Resultado
        atual={atual}
        registros={registros}
        aoAplicar={(novo) => {
          setRegistros([]);
          aplicar(novo);
        }}
        aoRecarregar={() => {
          setRegistros([]);
          void carregar();
        }}
      />
    );
  }

  return <Montagem opcoes={opcoes} atual={atual} aoMontar={aplicar} />;
}

function Moldura({ children }: { children: ReactNode }) {
  return (
    <div className="tela">
      <div className="tela__corpo">
        <CabecalhoDePagina titulo="Estudar" />
        {children}
      </div>
    </div>
  );
}

// -------------------------------------------------------------- montagem

/**
 * Montar a sessão — **uma coluna, um painel, três blocos** (Fase 3).
 *
 * Ela era cinco cartões empilhados: "Como montar" com três interruptores,
 * "Categorias" com uma árvore inteira de caixas, "Tipo de item", "Dificuldade",
 * e o botão flutuando numa barra de ação por cima do conteúdo. Em 1280×800 nada
 * disso cabia na tela, e montar uma sessão — que é o gesto mais repetido do app
 * — exigia rolar até o fim para achar o botão.
 *
 * Agora é um painel de ~680px com três blocos separados por fio fino: matéria e
 * assunto, a grade dos eixos que sobram, e os três interruptores. Abaixo dele a
 * linha que diz quantos itens o filtro alcança e quantos a sessão vai usar, e o
 * botão na largura do painel, no fluxo.
 *
 * **A contagem é do servidor, não uma conta local.** `estudo.disponiveis` roda
 * exatamente os mesmos filtros que a montagem, e é isso que faz a linha valer:
 * uma conta somada aqui com as contagens por opção erraria em todo filtro que
 * cruza dois eixos.
 */
function Montagem({
  opcoes,
  atual,
  aoMontar,
}: {
  opcoes: OpcoesEstudo;
  atual: EstudoAtual;
  aoMontar: (novo: EstudoAtual) => void;
}) {
  const { limites } = useSessao();
  const [filtros, setFiltros] = useState<MontarEntrada>({});
  const [quantidade, setQuantidade] = useState(limites.itensPorSessao);
  const [montando, setMontando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [disponiveis, setDisponiveis] = useState<number | null>(null);

  const materias = opcoes.categorias.filter((c) => c.profundidade === 0);
  const escolhidasIds = filtros.categoriaIds ?? [];

  /*
   * Os assuntos oferecidos são os das matérias escolhidas — e com nenhuma
   * escolhida, os de todas. Oferecer os assuntos do acervo inteiro
   * transformaria o segundo campo num índice, e não num recorte do primeiro.
   */
  const assuntos = useMemo(() => {
    const raizesEscolhidas = materias.filter((m) => escolhidasIds.includes(m.id));
    const alvo = raizesEscolhidas.length > 0 ? raizesEscolhidas : materias;
    const dentro: typeof opcoes.categorias = [];
    let atualRaiz: string | null = null;
    for (const c of opcoes.categorias) {
      if (c.profundidade === 0) {
        atualRaiz = alvo.some((m) => m.id === c.id) ? c.id : null;
        continue;
      }
      if (atualRaiz !== null) dentro.push(c);
    }
    return dentro;
  }, [opcoes.categorias, materias, escolhidasIds]);

  const assuntosEscolhidos = (filtros.categoriaIds ?? []).filter((id) => assuntos.some((a) => a.id === id));
  const materiasEscolhidas = escolhidasIds.filter((id) => materias.some((m) => m.id === id));

  /*
   * O corpo que vai para o servidor — tanto para contar quanto para montar.
   * Um só, porque a promessa da linha "N disponíveis" é justamente a de que as
   * duas perguntas usam os mesmos filtros.
   */
  const corpo: MontarEntrada = useMemo(() => {
    /*
     * O servidor une os descendentes de cada id pedido. Mandar a matéria junto
     * com um assunto dela devolveria a matéria inteira — o assunto escolhido
     * seria ignorado. Matéria com assunto escolhido sai do corpo; só vai a
     * matéria que não teve recorte.
     */
    const ids = filtros.categoriaIds ?? [];
    if (ids.length === 0) return { ...filtros, quantidade };
    const raizDe = new Map<string, string>();
    let raiz: string | null = null;
    for (const c of opcoes.categorias) {
      if (c.profundidade === 0) raiz = c.id;
      else if (raiz !== null) raizDe.set(c.id, raiz);
    }
    const recortadas = new Set(ids.map((id) => raizDe.get(id)).filter((r): r is string => r !== undefined));
    return { ...filtros, categoriaIds: ids.filter((id) => !recortadas.has(id)), quantidade };
  }, [filtros, quantidade, opcoes.categorias]);

  /*
   * A contagem ao vivo, com 150 ms de folga. Sem o debounce, arrastar o campo
   * de quantidade dispara uma consulta por dígito; com ele, a linha acompanha
   * sem que o servidor conte dez vezes a mesma coisa.
   */
  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => {
      void pedir("estudo.disponiveis", { corpo })
        .then((r) => {
          if (vivo) setDisponiveis(r.disponiveis);
        })
        .catch(() => {
          // Sem a contagem a tela continua montável: a linha some, o botão fica.
          if (vivo) setDisponiveis(null);
        });
    }, 150);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [corpo]);

  const vaiUsar = disponiveis === null ? quantidade : Math.min(quantidade, disponiveis);
  const semItens = disponiveis === 0;

  const montar = useCallback(async () => {
    setMontando(true);
    setErro(null);
    try {
      const r = await pedir("estudo.montar", { corpo });
      if (!r.montada) {
        setErro("Nenhum item passou nesses filtros. Tire um deles e tente de novo.");
        setMontando(false);
        return;
      }
      aoMontar(r.atual);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível montar a sessão.");
      setMontando(false);
    }
  }, [corpo, aoMontar]);

  if (opcoes.total === 0) {
    return (
      <Moldura>
        <section className="estado">
          <h2 className="estado__titulo">Esta trilha ainda não tem itens</h2>
          <p className="estado__texto">Importe um lote para começar a estudar.</p>
          <Link className="botao" to="/acervo?aba=importar">
            Importar itens
          </Link>
        </section>
      </Moldura>
    );
  }

  const todosOsTipos = opcoes.tipos.map((t) => t.valor);
  const tiposEfetivos = (filtros.tipos ?? []).length > 0 ? (filtros.tipos as TipoItem[]) : todosOsTipos;
  function alternarTipo(t: TipoItem) {
    const tem = tiposEfetivos.includes(t);
    const proximos = tem ? tiposEfetivos.filter((x) => x !== t) : [...tiposEfetivos, t];
    // Nenhum ou todos marcados é o mesmo que sem filtro.
    setFiltros((f) => ({ ...f, tipos: proximos.length === 0 || proximos.length === todosOsTipos.length ? [] : proximos }));
  }
  // Mesma regra do tipo: todas acesas é sem filtro, e o clique apaga ou acende.
  const todasAsDificuldades = opcoes.dificuldades.map((d) => d.valor);
  const dificuldadesEfetivas =
    (filtros.dificuldades ?? []).length > 0 ? (filtros.dificuldades as string[]) : todasAsDificuldades;
  function alternarDificuldade(d: string) {
    const tem = dificuldadesEfetivas.includes(d);
    const proximas = tem ? dificuldadesEfetivas.filter((x) => x !== d) : [...dificuldadesEfetivas, d];
    setFiltros((f) => ({
      ...f,
      dificuldades: proximas.length === 0 || proximas.length === todasAsDificuldades.length ? [] : proximas,
    }));
  }

  return (
    <form
      className="montagem-caderno"
      onSubmit={(e) => {
        e.preventDefault();
        if (!montando && !semItens) void montar();
      }}
    >
      <div className="montagem-caderno__principal">
        <CabecalhoDePagina titulo="Montar sessão" subtitulo="Filtros opcionais. Sem nenhum, a sessão sorteia do acervo inteiro." />

        {atual.sessaoExpirada && (
          <p className="campo__erro" role="status">
            A sessão anterior expirou depois de {limites.sessaoInatividadeMinutos} minutos sem atividade. O que você
            respondeu está guardado; monte uma sessão nova para continuar.
          </p>
        )}

        <div className="filtros-mao">
          <div className="filtros-mao__linha">
            <span className="t-anotacao t-2 filtros-mao__rotulo" id="rot-materia">
              matéria
            </span>
            <SeletorMultiplo
              rotulo="Matéria"
              idDoRotulo="rot-materia"
              opcoes={materias.map((m) => ({ valor: m.id, rotulo: m.nome, contagem: m.itens }))}
              escolhidos={materiasEscolhidas}
              vazio="todas"
              aoMudar={(novas) =>
                setFiltros((f) => ({
                  ...f,
                  categoriaIds: [
                    ...novas,
                    ...(f.categoriaIds ?? []).filter((id) =>
                      opcoes.categorias.some((c) => c.id === id && c.profundidade > 0),
                    ),
                  ],
                }))
              }
            />
          </div>

          <div className="filtros-mao__linha">
            <span className="t-anotacao t-2 filtros-mao__rotulo" id="rot-assunto">
              assunto
            </span>
            <SeletorMultiplo
              rotulo="Assunto"
              idDoRotulo="rot-assunto"
              opcoes={assuntos.map((a) => ({ valor: a.id, rotulo: a.nome, contagem: a.itens }))}
              escolhidos={assuntosEscolhidos}
              vazio="todos"
              aoMudar={(novos) => setFiltros((f) => ({ ...f, categoriaIds: [...materiasEscolhidas, ...novos] }))}
            />
          </div>

          <div className="filtros-mao__linha">
            <span className="t-anotacao t-2 filtros-mao__rotulo" id="rot-tipo">
              tipo de item
            </span>
            <div className="fila-mao" role="group" aria-labelledby="rot-tipo">
              {opcoes.tipos.map((t) => (
                <button
                  key={t.valor}
                  type="button"
                  className="chip-mao"
                  aria-pressed={tiposEfetivos.includes(t.valor)}
                  title={`${t.itens} itens`}
                  onClick={() => alternarTipo(t.valor)}
                >
                  {NOME_DO_TIPO[t.valor].toLocaleLowerCase("pt-BR")}
                </button>
              ))}
            </div>
          </div>

          {opcoes.dificuldades.length > 0 && (
            <div className="filtros-mao__linha">
              <span className="t-anotacao t-2 filtros-mao__rotulo" id="rot-dif">
                dificuldade
              </span>
              <div className="fila-mao" role="group" aria-labelledby="rot-dif">
                {opcoes.dificuldades.map((d) => (
                  <button
                    key={d.valor}
                    type="button"
                    className={`etiqueta-dif etiqueta-dif--${grauDaDificuldade(d.valor)} etiqueta-dif--botao`}
                    aria-pressed={dificuldadesEfetivas.includes(d.valor)}
                    title={`${d.itens} itens`}
                    onClick={() => alternarDificuldade(d.valor)}
                  >
                    {d.valor}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="filtros-mao__linha">
            <label className="t-anotacao t-2 filtros-mao__rotulo" htmlFor="montagem-quantidade">
              quantidade
            </label>
            <Contador
              id="montagem-quantidade"
              rotulo="Quantidade"
              min={1}
              max={Math.max(disponiveis ?? limites.itensPorSessao, 1)}
              valor={quantidade}
              aoMudar={(n) => setQuantidade(Math.floor(n))}
            />
          </div>
        </div>

        <div className="linha-vazia" />

        <div className="caixas-mao">
          <CaixaDeMarcar
            rotulo="Só o que nunca respondi"
            contagem={opcoes.naoRespondidos}
            marcada={filtros.soNaoRespondidos ?? false}
            aoMudar={(v) => setFiltros((f) => ({ ...f, soNaoRespondidos: v, soErradosAntes: v ? false : f.soErradosAntes }))}
          />
          <CaixaDeMarcar
            rotulo="Só o que já errei"
            contagem={opcoes.naFilaDeErradas}
            marcada={filtros.soErradosAntes ?? false}
            desabilitada={opcoes.naFilaDeErradas === 0}
            aoMudar={(v) => setFiltros((f) => ({ ...f, soErradosAntes: v, soNaoRespondidos: v ? false : f.soNaoRespondidos }))}
          />
          <CaixaDeMarcar
            rotulo="Primeiro os que mais errei"
            ajuda="muda a ordem, não o conjunto"
            marcada={filtros.priorizarMaisErrados ?? false}
            aoMudar={(v) => setFiltros((f) => ({ ...f, priorizarMaisErrados: v }))}
          />
        </div>

        {erro && (
          <p className="campo__erro" role="alert">
            {erro}
          </p>
        )}
      </div>

      <aside className="montagem-caderno__lado">
        <section className="cartao-mao montagem-caderno__resumo" aria-labelledby="sessao-titulo">
          <h2 className="t-secao" id="sessao-titulo">
            Esta sessão
          </h2>
          <dl className="resumo" aria-live="polite">
            <div className="resumo__linha">
              <dt className="t-ajuda">disponíveis</dt>
              <dd className="t-secao">{disponiveis === null ? "…" : disponiveis}</dd>
            </div>
            <div className="resumo__linha">
              <dt className="t-ajuda">a sessão usa</dt>
              <dd className="t-secao">{semItens ? 0 : vaiUsar}</dd>
            </div>
            <div className="resumo__linha">
              <dt className="t-ajuda">ordem</dt>
              <dd className="t-secao">{filtros.priorizarMaisErrados ? "mais erradas" : "sorteada"}</dd>
            </div>
          </dl>
          {semItens && <p className="t-ajuda">Nenhum item passa por esses filtros. Tire um deles.</p>}
        </section>

        <button className="botao montagem-caderno__comecar" type="submit" disabled={montando || semItens}>
          {montando ? "Montando…" : "Começar sessão"}
          <Icone glifo={IconeSeguir} papel="acao" />
        </button>

        <DuvidasAbertas />
      </aside>
    </form>
  );
}

/** A caixinha à mão da montagem: o `<input>` de verdade, com o quadrado desenhado. */
function CaixaDeMarcar({
  rotulo,
  contagem,
  ajuda,
  marcada,
  desabilitada,
  aoMudar,
}: {
  rotulo: string;
  contagem?: number;
  ajuda?: string;
  marcada: boolean;
  desabilitada?: boolean;
  aoMudar: (v: boolean) => void;
}) {
  return (
    <label className={desabilitada ? "caixa-linha caixa-linha--desabilitada" : "caixa-linha"}>
      <input
        type="checkbox"
        className="so-leitor"
        checked={marcada}
        disabled={desabilitada}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      <CaixaMao marcada={marcada} />
      <span className="t-corpo">
        <b>{rotulo}</b>
        {contagem !== undefined && <span className="t-2"> ({contagem})</span>}
        {ajuda && <span className="t-2 caixa-linha__ajuda">{ajuda}</span>}
      </span>
    </label>
  );
}

/**
 * O post-it de dúvida aberta (8a): enquanto há dúvida aberta numa categoria,
 * os itens de lá entram primeiro na montagem (regra da aba Fontes).
 */
function DuvidasAbertas() {
  const [abertas, setAbertas] = useState<{ categoria: string; n: number }[]>([]);
  useEffect(() => {
    let vivo = true;
    void pedir("duvida.listar")
      .then((r) => {
        if (!vivo) return;
        const porCategoria = new Map<string, number>();
        for (const d of r.duvidas) {
          if (d.resolvida) continue;
          const nome = d.categoria.at(-1) ?? "Sem categoria";
          porCategoria.set(nome, (porCategoria.get(nome) ?? 0) + 1);
        }
        setAbertas([...porCategoria].map(([categoria, n]) => ({ categoria, n })));
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, []);
  if (abertas.length === 0) return null;
  const total = abertas.reduce((s, a) => s + a.n, 0);
  return (
    <aside className="postit postit--rosa montagem-caderno__duvida">
      <p className="postit__titulo">{total === 1 ? "Dúvida aberta" : "Dúvidas abertas"}</p>
      {abertas.slice(0, 3).map((a) => (
        <p className="postit__texto" key={a.categoria}>
          {a.categoria} tem {a.n} {a.n === 1 ? "dúvida aberta" : "dúvidas abertas"}.
        </p>
      ))}
      <p className="postit__texto">Os itens de lá entram primeiro.</p>
    </aside>
  );
}

// -------------------------------------------------------------- resultado

function Resultado({
  atual,
  registros,
  aoAplicar,
  aoRecarregar,
}: {
  atual: EstudoAtual;
  registros: readonly RegistroDaSessao[];
  aoAplicar: (novo: EstudoAtual) => void;
  aoRecarregar: () => void;
}) {
  const [encerrando, setEncerrando] = useState(false);
  const navegar = useNavigate();

  async function fechar() {
    setEncerrando(true);
    await pedir("estudo.encerrar", { corpo: { motivo: "concluida" } }).catch(() => undefined);
  }

  return (
    <FimDeSessao
      atual={resumoDoAtual(atual)}
      registros={registros}
      encerrando={encerrando}
      aoEncerrar={() => {
        void fechar().then(() => navegar("/"));
      }}
      aoEstudarMais={() => {
        void fechar()
          .then(() => pedir("estudo.montar", { corpo: { quantidade: atual.metaDiaria } }))
          .then((r) => (r.montada ? aoAplicar(r.atual) : aoRecarregar()))
          .catch(() => aoRecarregar())
          .finally(() => setEncerrando(false));
      }}
    />
  );
}
